import React, { useState, useRef, useEffect } from 'react';
import { Phone, Instagram, Facebook, Snowflake, Trash2, X, Building2, Clock, MapPin, DollarSign, ArrowRight, Sofa, Leaf, Home, AlertTriangle } from 'lucide-react';
import emailjs from '@emailjs/browser';
import './App.css';

export default function App() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    message: ''
  });

  const [showPopup, setShowPopup] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const videoRef = useRef(null);
  const junkSectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          videoRef.current?.play();
        } else {
          videoRef.current?.pause();
        }
      },
      { threshold: 0.2 }
    );
    if (junkSectionRef.current) observer.observe(junkSectionRef.current);
    return () => observer.disconnect();
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');

    try {
      await emailjs.send(
        'service_p1yropo',
        'template_xw4vbmm',
        {
          from_name: formData.name,
          user_email: formData.email,
          phone: formData.phone,
          address: formData.address,
          message: formData.message
        },
        'vyHJ72V_rHLB6xKaU'
      );

      setShowPopup(true);

      setFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        message: ''
      });

      setTimeout(() => {
        setShowPopup(false);
      }, 3000);

    } catch (error) {
      console.error('EmailJS Error:', error);
      setErrorMessage('Failed to send message. Please try again or call us directly.');
    }

    setIsSubmitting(false);
  };

  const junkTiles = [
    { icon: Sofa,          label: 'Furniture & Appliances',       desc: 'Sofas, fridges, mattresses & more' },
    { icon: Trash2,        label: 'Residential Cleanouts',        desc: 'Garages, basements, pre & post-move' },
    { icon: Building2,     label: 'Commercial & Renovation',      desc: 'Offices, retail, construction debris' },
    { icon: Leaf,          label: 'Outdoor Yard Waste',           desc: 'Branches, soil, lawn debris & more' },
    { icon: Home,          label: 'Shed Demolition',              desc: 'Full teardown and debris removal' },
    { icon: AlertTriangle, label: 'Hazardous & Special Waste',    desc: 'Safe handling of problem materials' },
    { icon: DollarSign,    label: 'Upfront Pricing',              desc: 'No hidden fees, ever' },
    { icon: Clock,         label: 'Same-Day or Next-Day Service', desc: 'Fast response, Sudbury-wide' },
    { icon: MapPin,        label: 'Sudbury & Area',               desc: 'Local, trusted, reliable' },
  ];

  const navBtnStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 14px',
    borderRadius: '8px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#94a3b8',
    fontSize: '14px',
    fontWeight: 600,
    transition: 'color 0.2s',
    fontFamily: 'Inter, sans-serif',
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 16px',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '10px',
    color: '#f1f5f9',
    fontSize: '15px',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'Inter, sans-serif',
  };

  return (
    <div style={{ backgroundColor: '#0a1628', fontFamily: 'Inter, sans-serif', color: '#f1f5f9', minHeight: '100vh' }}>

      {/* ── STICKY NAV ── */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
        backgroundColor: 'rgba(10,22,40,0.92)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '64px' }}>
          <button onClick={() => scrollToSection('header')} style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'none', border: 'none', cursor: 'pointer' }}>
            <img src="/logowhite.png" alt="Alans Group" style={{ height: '48px', width: 'auto', display: 'block' }} />
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <button
              onClick={() => scrollToSection('junk-removal')}
              style={navBtnStyle}
              onMouseEnter={e => e.currentTarget.style.color = '#f1f5f9'}
              onMouseLeave={e => e.currentTarget.style.color = '#94a3b8'}
            >
              <Trash2 size={16} />
              <span className="hidden sm:inline">Junk Removal</span>
            </button>
            <button
              onClick={() => scrollToSection('snow-plowing')}
              style={navBtnStyle}
              onMouseEnter={e => e.currentTarget.style.color = '#f1f5f9'}
              onMouseLeave={e => e.currentTarget.style.color = '#94a3b8'}
            >
              <Snowflake size={16} />
              <span className="hidden sm:inline">Snow Plowing</span>
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              style={navBtnStyle}
              onMouseEnter={e => e.currentTarget.style.color = '#f1f5f9'}
              onMouseLeave={e => e.currentTarget.style.color = '#94a3b8'}
            >
              <Phone size={16} />
              <span className="hidden sm:inline">Contact</span>
            </button>
          </div>
        </div>
      </nav>

      {/* ── MAIN WRAPPER ── */}
      <div className="pt-16">

        {/* ── SECTION 1 — HERO ── */}
        <section id="header" style={{ position: 'relative', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', padding: '0 0 80px', overflow: 'hidden' }}>
          {/* Logo */}
          <div style={{ position: 'relative', zIndex: 10, width: '100%', marginBottom: '32px', marginTop: '5px' }}>
            <img
              src="/logowhite.png"
              alt="Alans Group Logo"
              style={{ width: '100%', maxWidth: '800px', height: 'auto', display: 'block', margin: '0 auto' }}
            />
          </div>

          {/* Hero text content */}
          <div style={{ position: 'relative', zIndex: 10, textAlign: 'center', maxWidth: '800px', padding: '0 16px' }}>
            <p style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: 'clamp(0.9rem, 2vw, 1.1rem)', color: '#94a3b8', letterSpacing: '0.18em', textTransform: 'uppercase', margin: '0 0 6px' }}>
              Sudbury's Trusted
            </p>
            <h1 style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 900, fontSize: 'clamp(2rem, 5vw, 3.5rem)', lineHeight: 1.15, margin: '0 0 24px', background: 'linear-gradient(90deg, #3b82f6, #16a34a)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              Snow &amp; Junk Experts
            </h1>
            <p style={{ color: '#94a3b8', fontSize: 'clamp(0.9rem, 2vw, 1.05rem)', marginBottom: '44px' }}>
              Professional services for Sudbury &amp; surrounding areas
            </p>

            {/* CTA Cards */}
            <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => scrollToSection('junk-removal')}
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  border: '1px solid rgba(22,163,74,0.45)',
                  borderRadius: '16px',
                  padding: '28px 32px',
                  minWidth: '210px',
                  cursor: 'pointer',
                  color: '#f1f5f9',
                  textAlign: 'center',
                  transition: 'all 0.3s',
                  boxShadow: '0 0 24px rgba(22,163,74,0.12)',
                }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 0 32px rgba(22,163,74,0.35)'; e.currentTarget.style.transform = 'translateY(-4px)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 0 24px rgba(22,163,74,0.12)'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <Trash2 style={{ width: '44px', height: '44px', color: '#16a34a', margin: '0 auto 12px', display: 'block' }} />
                <div style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '17px', marginBottom: '8px' }}>JUNK REMOVAL</div>
                <div style={{ color: '#94a3b8', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                  Get a free quote <ArrowRight size={13} />
                </div>
              </button>

              <button
                onClick={() => scrollToSection('snow-plowing')}
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  border: '1px solid rgba(59,130,246,0.45)',
                  borderRadius: '16px',
                  padding: '28px 32px',
                  minWidth: '210px',
                  cursor: 'pointer',
                  color: '#f1f5f9',
                  textAlign: 'center',
                  transition: 'all 0.3s',
                  boxShadow: '0 0 24px rgba(59,130,246,0.12)',
                }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 0 32px rgba(59,130,246,0.35)'; e.currentTarget.style.transform = 'translateY(-4px)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 0 24px rgba(59,130,246,0.12)'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <Snowflake style={{ width: '44px', height: '44px', color: '#3b82f6', margin: '0 auto 12px', display: 'block' }} />
                <div style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '17px', marginBottom: '8px' }}>SNOW PLOWING</div>
                <div style={{ color: '#94a3b8', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                  View pricing <ArrowRight size={13} />
                </div>
              </button>
            </div>
          </div>
        </section>

        {/* ── TRUST BAR ── */}
        <div style={{ backgroundColor: '#0f1f35', borderTop: '1px solid rgba(255,255,255,0.08)', borderBottom: '1px solid rgba(255,255,255,0.08)', padding: '36px 16px' }}>
          <div style={{ maxWidth: '800px', margin: '0 auto', display: 'flex', justifyContent: 'center', alignItems: 'stretch', flexWrap: 'wrap' }}>
            {[
              { stat: '500+',      label: 'Driveways Cleared',  sub: 'And counting every winter' },
              { stat: 'Same/Next-Day',  label: 'Service',        sub: 'Fast response guaranteed' },
              { stat: 'Sudbury',   label: 'Local & Trusted',    sub: 'Serving Greater Sudbury' },
            ].map((item, i, arr) => (
              <div
                key={i}
                style={{
                  flex: '1',
                  minWidth: '160px',
                  textAlign: 'center',
                  padding: '16px 24px',
                  borderRight: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.08)' : 'none',
                }}
              >
                <div style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 900, fontSize: 'clamp(1.4rem, 3.5vw, 2rem)', color: '#f1f5f9', marginBottom: '2px' }}>{item.stat}</div>
                <div style={{ fontWeight: 600, fontSize: '14px', color: '#cbd5e1', marginBottom: '4px' }}>{item.label}</div>
                <div style={{ fontSize: '12px', color: '#64748b' }}>{item.sub}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── SECTION 2 — JUNK REMOVAL ── */}
        <section id="junk-removal" ref={junkSectionRef} style={{ position: 'relative', minHeight: '100vh', padding: '80px 16px 100px', overflow: 'hidden', background: 'linear-gradient(180deg, #0a1628 0%, #071a0e 100%)' }}>
          {/* Background video */}
          <video
            ref={videoRef}
            muted
            loop
            playsInline
            style={{
              position: 'absolute', inset: 0,
              width: '100%', height: '100%',
              objectFit: 'cover',
              zIndex: 0,
              opacity: 0.6,
            }}
          >
            <source src="/junk_video.mp4" type="video/mp4" />
          </video>

          {/* Dark overlay for text readability */}
          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(180deg, rgba(10,26,40,0.75) 0%, rgba(7,26,14,0.80) 100%)',
            zIndex: 1,
          }} />


          <div style={{ maxWidth: '1100px', margin: '0 auto', position: 'relative', zIndex: 10 }}>
            {/* Section heading */}
            <div style={{ textAlign: 'center', marginBottom: '56px' }}>
              <h2 style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 900, fontSize: 'clamp(2rem, 5vw, 3.5rem)', color: '#f1f5f9', margin: '0 0 12px', letterSpacing: '0.05em' }}>
                JUNK REMOVAL
              </h2>
              <div style={{ width: '60px', height: '4px', background: '#16a34a', margin: '0 auto 16px', borderRadius: '2px' }} />
              <p style={{ color: '#94a3b8', fontSize: '18px', margin: 0 }}>Got junk? We haul it fast in Sudbury.</p>
            </div>

            {/* 2×3 icon tile grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', marginBottom: '48px' }}>
              {junkTiles.map(({ icon: Icon, label, desc }, i) => (
                <div
                  key={i}
                  style={{
                    background: 'rgba(0,0,0,0.55)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.09)',
                    borderRadius: '16px',
                    padding: '24px 20px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '16px',
                    transition: 'all 0.25s',
                    cursor: 'default',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(22,163,74,0.4)'; e.currentTarget.style.transform = 'translateY(-3px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.09)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: 'rgba(22,163,74,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon style={{ color: '#16a34a', width: '22px', height: '22px' }} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '15px', color: '#f1f5f9', marginBottom: '4px' }}>{label}</div>
                    <div style={{ fontSize: '13px', color: '#94a3b8' }}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div style={{ textAlign: 'center' }}>
              <button
                onClick={() => scrollToSection('contact')}
                style={{
                  background: '#16a34a',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '16px 36px',
                  fontSize: '17px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.25s',
                  boxShadow: '0 4px 20px rgba(22,163,74,0.3)',
                  fontFamily: 'Inter, sans-serif',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#15803d'; e.currentTarget.style.boxShadow = '0 4px 28px rgba(22,163,74,0.5)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#16a34a'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(22,163,74,0.3)'; }}
              >
                Get a Free Quote <ArrowRight size={18} />
              </button>
            </div>
          </div>

          {/* Wave divider */}
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, lineHeight: 0 }}>
            <svg viewBox="0 0 1440 60" preserveAspectRatio="none" style={{ width: '100%', height: '60px', display: 'block' }}>
              <path d="M0,30 C360,60 1080,0 1440,30 L1440,60 L0,60 Z" fill="#0a1628" />
            </svg>
          </div>
        </section>

        {/* ── SECTION 3 — SNOW PLOWING ── */}
        <section id="snow-plowing" style={{ position: 'relative', minHeight: '100vh', overflow: 'hidden', background: 'linear-gradient(180deg, #0a1628 0%, #060f28 100%)', padding: '80px 16px 100px' }}>
          {/* Falling snow */}
          <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
            {[...Array(80)].map((_, i) => (
              <div
                key={i}
                className="animate-fall"
                style={{
                  position: 'absolute',
                  left: `${(i * 1.27 + 1) % 100}%`,
                  top: `-${(i * 5) % 20}px`,
                  animationDelay: `${(i * 0.063) % 5}s`,
                  animationDuration: `${5 + (i % 5)}s`,
                  color: '#93c5fd',
                  fontSize: '18px',
                  opacity: 0.35,
                }}
              >
                ❄
              </div>
            ))}

            {/* Plow trucks */}
            <div className="animate-plow" style={{ position: 'absolute', top: '15%', animationDelay: '0s' }}>
              <img src="/snow_truck.png" alt="Snow plow truck" style={{ height: '140px', width: 'auto', filter: 'drop-shadow(0 4px 16px rgba(0,0,0,0.5))', opacity: 0.7, transform: 'scaleX(-1)' }} />
            </div>
            <div className="animate-plow" style={{ position: 'absolute', top: '72%', animationDelay: '5s' }}>
              <img src="/snow_truck.png" alt="Snow plow truck" style={{ height: '120px', width: 'auto', filter: 'drop-shadow(0 4px 16px rgba(0,0,0,0.5))', opacity: 0.6, transform: 'scaleX(-1)' }} />
            </div>
          </div>

          <div style={{ maxWidth: '1100px', margin: '0 auto', position: 'relative', zIndex: 10 }}>
            {/* Section heading */}
            <div style={{ textAlign: 'center', marginBottom: '56px' }}>
              <h2 style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 900, fontSize: 'clamp(2rem, 5vw, 3.5rem)', color: '#f1f5f9', margin: '0 0 12px', letterSpacing: '0.05em' }}>
                SNOW PLOWING
              </h2>
              <div style={{ width: '60px', height: '4px', background: '#3b82f6', margin: '0 auto 16px', borderRadius: '2px' }} />
              <p style={{ color: '#94a3b8', fontSize: '18px', margin: 0 }}>Reliable winter snow removal for Sudbury driveways.</p>
            </div>

            {/* Pricing cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(270px, 1fr))', gap: '24px' }}>

              {/* Seasonal */}
              <div style={{
                background: 'rgba(255,255,255,0.04)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                border: '1px solid rgba(59,130,246,0.3)',
                borderRadius: '20px',
                padding: '36px 28px',
                textAlign: 'center',
                boxShadow: '0 0 30px rgba(59,130,246,0.08)',
              }}>
                <Snowflake style={{ width: '40px', height: '40px', color: '#3b82f6', margin: '0 auto 16px', display: 'block' }} />
                <div style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '18px', color: '#f1f5f9', marginBottom: '16px', letterSpacing: '0.05em' }}>SEASONAL</div>
                <div style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 900, fontSize: 'clamp(2.5rem, 5vw, 3.5rem)', color: '#3b82f6', lineHeight: 1, marginBottom: '8px' }}>$699+</div>
                <div style={{ color: '#94a3b8', fontSize: '14px', marginBottom: '6px' }}>Unlimited Visits</div>
                <div style={{ color: '#475569', fontSize: '13px' }}>Rates vary by driveway size</div>
              </div>

              {/* Monthly — Popular */}
              <div style={{
                background: 'rgba(59,130,246,0.08)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                border: '1px solid rgba(59,130,246,0.5)',
                borderRadius: '20px',
                padding: '36px 28px',
                textAlign: 'center',
                boxShadow: '0 0 40px rgba(59,130,246,0.18)',
                position: 'relative',
              }}>
                <div style={{ position: 'absolute', top: '-14px', left: '50%', transform: 'translateX(-50%)', background: '#3b82f6', color: '#fff', fontSize: '11px', fontWeight: 700, padding: '4px 18px', borderRadius: '20px', letterSpacing: '0.12em', whiteSpace: 'nowrap' }}>
                  POPULAR
                </div>
                <Snowflake style={{ width: '40px', height: '40px', color: '#3b82f6', margin: '0 auto 16px', display: 'block' }} />
                <div style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '18px', color: '#f1f5f9', marginBottom: '16px', letterSpacing: '0.05em' }}>MONTHLY</div>
                <div style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 900, fontSize: 'clamp(2.5rem, 5vw, 3.5rem)', color: '#3b82f6', lineHeight: 1, marginBottom: '8px' }}>
                  $199<span style={{ fontSize: '1.2rem' }}>/mo</span>
                </div>
                <div style={{ color: '#475569', fontSize: '13px' }}>Rates vary by driveway size</div>
              </div>

              {/* One-Time */}
              <div style={{
                background: 'rgba(255,255,255,0.04)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                border: '1px solid rgba(245,158,11,0.3)',
                borderRadius: '20px',
                padding: '36px 28px',
                textAlign: 'center',
                boxShadow: '0 0 30px rgba(245,158,11,0.08)',
              }}>
                <Snowflake style={{ width: '40px', height: '40px', color: '#f59e0b', margin: '0 auto 16px', display: 'block' }} />
                <div style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '18px', color: '#f1f5f9', marginBottom: '4px', letterSpacing: '0.05em' }}>ONE-TIME</div>
                <div style={{ color: '#94a3b8', fontSize: '14px', marginBottom: '12px' }}>Flat Rate</div>
                <div style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 900, fontSize: 'clamp(2.5rem, 5vw, 3.5rem)', color: '#f59e0b', lineHeight: 1, marginBottom: '8px' }}>$59.99</div>
                <div style={{ color: '#475569', fontSize: '13px', fontStyle: 'italic' }}>Perfect for occasional or emergency needs</div>
              </div>

            </div>
          </div>

          {/* Wave divider */}
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, lineHeight: 0 }}>
            <svg viewBox="0 0 1440 60" preserveAspectRatio="none" style={{ width: '100%', height: '60px', display: 'block' }}>
              <path d="M0,0 C360,60 1080,0 1440,30 L1440,60 L0,60 Z" fill="#0f1f35" />
            </svg>
          </div>
        </section>

        {/* ── SECTION 4 — CONTACT & FOOTER ── */}
        <section id="contact" style={{ background: '#0f1f35', padding: '80px 16px 120px' }}>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>

            {/* Contact info */}
            <div style={{ textAlign: 'center', marginBottom: '56px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '20px' }}>
                <img src="/logowhite.png" alt="Alans Group" style={{ height: '80px', width: 'auto', display: 'block' }} />
                <h2 style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 900, fontSize: '1.8rem', color: '#f1f5f9', margin: 0 }}>CONTACT US</h2>
              </div>

              <a
                href="tel:705-923-5277"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', fontWeight: 900, fontFamily: 'Montserrat, sans-serif', color: '#3b82f6', textDecoration: 'none', marginBottom: '24px' }}
              >
                <Phone style={{ width: '32px', height: '32px' }} />
                705-923-5277
              </a>

              <div style={{ display: 'flex', justifyContent: 'center', gap: '28px', flexWrap: 'wrap' }}>
                <a
                  href="https://www.instagram.com/alans__group"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#94a3b8', textDecoration: 'none', fontSize: '15px', transition: 'color 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.color = '#f1f5f9'}
                  onMouseLeave={e => e.currentTarget.style.color = '#94a3b8'}
                >
                  <Instagram style={{ width: '22px', height: '22px' }} />
                  alans__group
                </a>
                <a
                  href="https://www.facebook.com/alansgroup"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#94a3b8', textDecoration: 'none', fontSize: '15px', transition: 'color 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.color = '#f1f5f9'}
                  onMouseLeave={e => e.currentTarget.style.color = '#94a3b8'}
                >
                  <Facebook style={{ width: '22px', height: '22px' }} />
                  alans_group
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div style={{
              background: 'rgba(255,255,255,0.03)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.09)',
              borderRadius: '20px',
              padding: '40px 32px',
              marginBottom: '48px',
            }}>
              <h3 style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '1.4rem', color: '#f1f5f9', textAlign: 'center', margin: '0 0 32px' }}>
                Send Us a Message
              </h3>

              <form onSubmit={handleSubmit}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px', marginBottom: '20px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#94a3b8', marginBottom: '8px' }}>Name *</label>
                    <input
                      type="text" id="name" name="name"
                      value={formData.name} onChange={handleInputChange} required
                      placeholder="Your full name"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#94a3b8', marginBottom: '8px' }}>Email *</label>
                    <input
                      type="email" id="email" name="email"
                      value={formData.email} onChange={handleInputChange} required
                      placeholder="your.email@example.com"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#94a3b8', marginBottom: '8px' }}>Phone *</label>
                    <input
                      type="tel" id="phone" name="phone"
                      value={formData.phone} onChange={handleInputChange} required
                      placeholder="(555) 123-4567"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#94a3b8', marginBottom: '8px' }}>Address *</label>
                    <input
                      type="text" id="address" name="address"
                      value={formData.address} onChange={handleInputChange} required
                      placeholder="Your address"
                      style={inputStyle}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '28px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#94a3b8', marginBottom: '8px' }}>Message *</label>
                  <textarea
                    id="message" name="message"
                    value={formData.message} onChange={handleInputChange} required
                    rows="5"
                    placeholder="Tell us about your service needs..."
                    style={{ ...inputStyle, resize: 'none' }}
                  />
                </div>

                <div style={{ textAlign: 'center' }}>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    style={{
                      background: '#3b82f6',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '10px',
                      padding: '14px 44px',
                      fontSize: '16px',
                      fontWeight: 700,
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      opacity: isSubmitting ? 0.6 : 1,
                      transition: 'all 0.25s',
                      boxShadow: '0 4px 20px rgba(59,130,246,0.3)',
                      fontFamily: 'Inter, sans-serif',
                    }}
                    onMouseEnter={e => { if (!isSubmitting) e.currentTarget.style.boxShadow = '0 4px 30px rgba(59,130,246,0.6)'; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 4px 20px rgba(59,130,246,0.3)'; }}
                  >
                    {isSubmitting ? 'Sending...' : 'Submit'}
                  </button>

                  {errorMessage && (
                    <p style={{ marginTop: '16px', color: '#f87171', fontWeight: 600 }}>{errorMessage}</p>
                  )}
                </div>
              </form>
            </div>

            {/* Footer */}
            <footer style={{ textAlign: 'center', paddingBottom: '8px' }}>
              <img src="/logowhite.png" alt="Alans Group" style={{ height: '60px', width: 'auto', margin: '0 auto 8px', display: 'block', opacity: 0.7 }} />
              <p style={{ color: '#475569', fontSize: '13px', margin: 0 }}>
                © 2025 Alans Group of Companies Inc. All rights reserved.
              </p>
            </footer>

          </div>
        </section>

      </div>

      {/* ── SUCCESS POPUP ── */}
      {showPopup && (
        <div style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '16px' }}>
          <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)' }} onClick={() => setShowPopup(false)} />
          <div
            className="animate-popup"
            style={{
              position: 'relative',
              background: '#0f1f35',
              border: '1px solid rgba(22,163,74,0.3)',
              borderRadius: '20px',
              padding: '40px 32px',
              maxWidth: '400px',
              width: '100%',
              textAlign: 'center',
              boxShadow: '0 0 48px rgba(22,163,74,0.2)',
            }}
          >
            <button
              onClick={() => setShowPopup(false)}
              style={{ position: 'absolute', top: '16px', right: '16px', background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
            >
              <X size={20} />
            </button>
            <div style={{ width: '64px', height: '64px', background: 'rgba(22,163,74,0.12)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
              <svg style={{ width: '32px', height: '32px', color: '#16a34a' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '1.4rem', color: '#f1f5f9', margin: '0 0 8px' }}>Message Sent!</h3>
            <p style={{ color: '#94a3b8', margin: 0 }}>Thank you for contacting us. We'll get back to you shortly.</p>
          </div>
        </div>
      )}

      {/* ── FAB — desktop only ── */}
      <a
        href="tel:705-923-5277"
        style={{
          position: 'fixed',
          bottom: '76px',
          right: '24px',
          zIndex: 200,
          display: 'flex',
          background: '#16a34a',
          color: '#fff',
          borderRadius: '50px',
          padding: '14px 22px',
          textDecoration: 'none',
          alignItems: 'center',
          gap: '8px',
          fontWeight: 700,
          fontSize: '15px',
          boxShadow: '0 4px 24px rgba(22,163,74,0.45)',
          fontFamily: 'Inter, sans-serif',
        }}
      >
        <Phone size={18} />
        Call Now
      </a>

      {/* ── MOBILE BOTTOM NAV ── */}
      <nav
        className="sm:hidden"
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          background: 'rgba(10,22,40,0.97)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          display: 'flex',
        }}
      >
        {[
          { icon: Trash2,    label: 'Junk',    section: 'junk-removal' },
          { icon: Snowflake, label: 'Snow',    section: 'snow-plowing' },
          { icon: Phone,     label: 'Contact', section: 'contact' },
        ].map(({ icon: Icon, label, section }) => (
          <button
            key={section}
            onClick={() => scrollToSection(section)}
            style={{
              flex: 1,
              padding: '12px 0',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#94a3b8',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '4px',
              fontSize: '11px',
              fontWeight: 600,
              fontFamily: 'Inter, sans-serif',
            }}
          >
            <Icon size={20} />
            {label}
          </button>
        ))}
      </nav>

    </div>
  );
}
